import { useTranslation } from 'react-i18next';
import { TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, Info } from 'lucide-react';
import { clsx } from 'clsx';
import { useCategoryTranslation } from '../../hooks/useCategoryTranslation';
import type { VariationItem } from '../../services/analytics';

interface TopVariationsCardProps {
  topIncreases: VariationItem[];
  topDecreases: VariationItem[];
  baselineLabel: string;
  comparisonLabel: string;
}

function formatAmount(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(Math.abs(value));
}

function formatPct(value: number | null): string {
  if (value === null) return 'N/A';
  if (Math.abs(value) < 0.5) return '0 %';
  return `${value > 0 ? '+' : ''}${value.toFixed(1)} %`;
}

// Une variation est favorable pour l'utilisateur si :
// - un revenu augmente
// - une dépense diminue
function isFavorable(item: VariationItem, variant: 'increase' | 'decrease'): boolean {
  if (variant === 'increase') return item.type === 'income';
  return item.type === 'expense';
}

export default function TopVariationsCard({
  topIncreases,
  topDecreases,
  baselineLabel,
  comparisonLabel,
}: TopVariationsCardProps) {
  const { t, i18n } = useTranslation('search');
  const { translateCategoryName } = useCategoryTranslation();
  const locale = i18n.language === 'en' ? 'en-GB' : 'fr-FR';

  const renderItem = (item: VariationItem, variant: 'increase' | 'decrease') => {
    const favorable = isFavorable(item, variant);
    const ArrowIcon = variant === 'increase' ? ArrowUpRight : ArrowDownRight;
    const sign = variant === 'increase' ? '+' : '−';
    const typeLabel = t(`analytics.variationType.${item.type}`);
    return (
      <li
        key={`${variant}-${item.categoryId}`}
        className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50"
      >
        <div className={clsx(
          'shrink-0 w-7 h-7 rounded-full flex items-center justify-center',
          favorable ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600',
        )}>
          <ArrowIcon className="w-3.5 h-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-medium text-gray-700 truncate">
              {translateCategoryName(item.categoryId, item.name, item.isDefault)}
            </span>
            <span className={clsx(
              'shrink-0 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded',
              item.type === 'income'
                ? 'bg-sky-50 text-sky-700'
                : 'bg-amber-50 text-amber-700',
            )}>
              {typeLabel}
            </span>
          </div>
          <div className="text-xs text-gray-400 truncate">
            {formatAmount(item.baselineTotal, locale)} → {formatAmount(item.comparisonTotal, locale)}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className={clsx(
            'text-sm font-semibold',
            favorable ? 'text-emerald-600' : 'text-rose-600',
          )}>
            {sign} {formatAmount(item.deltaAbs, locale)}
          </div>
          <div className="text-xs text-gray-500">{formatPct(item.deltaPct)}</div>
        </div>
      </li>
    );
  };

  const renderList = (items: VariationItem[], variant: 'increase' | 'decrease') => {
    if (items.length === 0) {
      return (
        <div className="text-sm text-gray-500 text-center py-6">
          {t('analytics.noVariation')}
        </div>
      );
    }
    return <ul className="space-y-2">{items.map((item) => renderItem(item, variant))}</ul>;
  };

  return (
    <div className="space-y-3">
      <div
        className="flex items-start gap-2 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2"
        title={t('analytics.variationLegend.title')}
      >
        <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-gray-400" />
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
            {t('analytics.variationLegend.favorable')}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-rose-500" />
            {t('analytics.variationLegend.unfavorable')}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-gray-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-800">{t('analytics.topIncreases')}</h3>
              <p className="text-xs text-gray-500">
                {t('analytics.fromTo', { from: baselineLabel, to: comparisonLabel })}
              </p>
            </div>
          </div>
          {renderList(topIncreases, 'increase')}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
              <TrendingDown className="w-4 h-4 text-gray-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-800">{t('analytics.topDecreases')}</h3>
              <p className="text-xs text-gray-500">
                {t('analytics.fromTo', { from: baselineLabel, to: comparisonLabel })}
              </p>
            </div>
          </div>
          {renderList(topDecreases, 'decrease')}
        </div>
      </div>
    </div>
  );
}
